// @ts-strict-ignore
import { LMLoggerService } from '@services/lm-logger.service';
import { Injectable } from '@angular/core';
import { LMConditionUtilService } from '@utils/lm-condition-util.service';
import { LandmarkPanel } from '@form/lm-form.model';
import { AppConstants } from '@utils/app.constants';
import { LMModel } from '@form/model';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { Observable, Observer } from 'rxjs';
import { LMSpecService } from '@services/lm-spec.service';
import { LMFormRequestService } from './lm-form-request.service';
import { LMCommonUtilService } from '@utils/lm-common-util.service';
import { LMFormState } from '@app/states';
import { LMContextService } from '@app/services';
import { LMUrlUtilService } from '@app/services';
import {LMWebAppService} from "@services/lm-webapp.service";

export type LMFieldIdMap = { [ key: string ]: Array<string> };

@Injectable()
export class LMFormPanelBaseService {
  private panelDisplayStateLastChanged: number;
  private panels: Array<LandmarkPanel>;
  private _panels: Array<LandmarkPanel>;
  private verticalTabs = false;
  private contextForm = undefined;
  private _furthestPanelIndex = 0;
  private _furthestSubPanelIndex = 0;
  private _mapFieldIds: LMFieldIdMap = {};

  constructor(
    private lmContextService: LMContextService,
    private lmWebAppService: LMWebAppService,
    private lmConditionUtilService: LMConditionUtilService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmLoggerService: LMLoggerService,
    private lmFormRequestService: LMFormRequestService,
    private lmSpecService: LMSpecService,
    private lmCommonUtilService: LMCommonUtilService,
    private lmUrlUtilService: LMUrlUtilService
  ) {}

  get furthestPanelIndex() {
    return this._furthestPanelIndex ;
  }
  set furthestPanelIndex(index: number) {
    this._furthestPanelIndex = index;
  }
  get furthestSubPanelIndex() {
    return this._furthestSubPanelIndex;
  }
  set furthestSubPanelIndex(index: number) {
    this._furthestSubPanelIndex = index;
  }

  public getPanelDisplayStateLastChanged(): number {
    return this.panelDisplayStateLastChanged;
  }

  public getPanels(): Array<LandmarkPanel> {
    return Object.assign([], this.panels);
  }

  public getVerticalTabs(): boolean {
    // No need to copy boolean values for immutability. primitives are immutable by
    // default and are compared by value, they don’t have individual identities.
    return this.verticalTabs;
  }

  public getContextForm(): LMContextForm {
    if (this.contextForm) {
      return Object.assign({}, this.contextForm);
    }
    return undefined;
  }

  // public setStepUnvisited(id: string): void {
  //   this.changeStepIcon(id, AppConstants.STEP_PROCESS_UNVISITED_ICON);
  // }
  //
  // public setStepInprocess(id: string): void {
  //   this.changeStepIcon(id, AppConstants.STEP_PROCESS_INPROCESS_ICON);
  // }
  //
  // public setStepCompleted(id: string): void {
  //   this.changeStepIcon(id, AppConstants.STEP_PROCESS_COMPLETE_ICON);
  // }
  //
  public setStepError(id: string): void {
    this.changeStepIcon(id, AppConstants.STEP_PROCESS_ERROR_ICON);
  }

  public changeStepIcon(id: string, icon: string): void {
    let panel: LandmarkPanel;
    for (let i = 0, len = this._panels.length; i < len; i++) {
      panel = this._panels[i];
      if (panel.id === id) {
        panel.icon = icon;
      }
    }
  }

  public getPanelIdByIndex(index: number): string {
    return this._panels[index].id;
  }

  public getPanelIndexById(id: string): number {
    for (let i = 0, len = this._panels.length; i < len; i++) {
      if (id === this._panels[i].id) {
        return i;
      }
    }
    return -1;
  }

  public getValidVisiblePanelIndexById(id: string): number {
    for (let i = 0, len = this.panels.length; i < len; i++) {
      if (id === this.panels[i].id && this.panels[i].isValid && this.panels[i].isVisible) {
        return i;
      }
    }
    return -1;
  }

  public updateCompositePanels(busFormSpec: LMBusFormSpec, model: LMModel, clearSubPanels: boolean, compositeForm?: boolean, createReportMode?: boolean) {
    this.panels = [];
    let panel: LandmarkPanel;
    const panelSpecs: Array<LMCompositePanelSpec> = busFormSpec.panels;
    let panelSpec: LMCompositePanelSpec;
    const dataView = model.getData();

    for (let i = 0, len = this._panels.length; i < len; i++) {
      panel = this._panels[ i ];

      // skip subPanels, they were added by their parent
      if (panel.isSubPanel) {
        continue;
      }

      panelSpec = panelSpecs[ i ];

      // wizard form has a finish form that is not included in the panelSpec collection, the finish form should
      // always be added to the list of panels

      panel.hidden = false;

      if (compositeForm && this.lmWebAppService.isFormPanelInaccessible(panelSpec)) {
        panel.hidden = true;
      }

      if (i <= this._furthestPanelIndex) {
        panel.linearVisited = true;
      }

      if (panelSpec) {
        panel.title = this.lmFormatUtilService.formatMessageRule(panelSpec.panelTitle, model, busFormSpec.fields);
      } else if (panel.finishForm) {
        panel.title = this.lmFormatUtilService.formatMessageRule(busFormSpec.wizardFinishForm.title, model, busFormSpec.fields);
      }

      if (panelSpec && panelSpec.visibility && !this.lmConditionUtilService.isValid(panelSpec.visibility, model)) {
        if (busFormSpec.panelNavigation || busFormSpec.isWizard) {
          continue;
        }
        panel.hidden = true;
      }

      let validPanel = (panelSpec && panelSpec.validity) ? this.lmConditionUtilService.isValid(panelSpec.validity, model) : true;

      if (panelSpec && !busFormSpec.isWizard && model.isCreateAction && this.isOTMPanel(panelSpec, model)) {
        validPanel = false;
      }

      // wizard pattern does not support visible when
      if (!validPanel && (busFormSpec.isWizard)) {
        continue;
      }

      if (createReportMode) {
        if (
          panel.type === AppConstants.FORM_PANELTYPE_FORMLIST 
          || panel.type === AppConstants.FORM_PANELTYPE_MULTILIST
          || panel.type === AppConstants.FORM_PANELTYPE_INSTANCECHART
          || panel.type === AppConstants.FORM_PANELTYPE_CHART
          || panel.type === AppConstants.FORM_PANELTYPE_TEMPLATEFORMLIST
          || panel.type === AppConstants.FORM_PANELTYPE_DASHBOARD
          || panel.type === AppConstants.FORM_PANELTYPE_ACTIONIS
        ) {
          validPanel = false;
          }
      }

      panel.isValid = validPanel;

      if (panelSpec && (busFormSpec.panelNavigation || busFormSpec.isWizard)) {
        this.updateStepIcon(panel, dataView);
      }

      if (panelSpec && panelSpec.panelType === 'dashboard' && !!panelSpec.background) {
        panel.background = panelSpec.background;
      }

      this.panels.push(panel);

      if (panel.hasSubPanels) {

        if (clearSubPanels) {
          // DPB TODO call folderClose on the panel.
          panel.subPanels = [];
        } else {
          // add the subpanels to the this.panels list
          this.addPanelSubPanels(busFormSpec, panel);
        }
      }

      if (panel.dashBoardPanes) {
        panel.dashBoardPanes.forEach(dashboardPanel => {
          this.updateDashBoardPanels(busFormSpec, model, dashboardPanel);
        });
      }
    }

    this.panelDisplayStateLastChanged = new Date().getTime();
  }

  private getNextAvailablePanelIndex(index: number): number {
    for (let n = 0, len = this.panels.length; n < len; n++) {
      if ((this.panels[n].index > index) && this.panels[n].isValid && this.panels[n].isVisible) {
        return this.panels[n].index;
      }
    }
    return -1;
  }

  private getPreviousAvailablePanelIndex(index: number): number {
    for (let n = this.panels.length -1; n >=0; n--) {
      if ((this.panels[n].index < index) && this.panels[n].isValid && this.panels[n].isVisible) {
        return this.panels[n].index;
      }
    }
    return -1;
  }

  public getFurthestVisitedCompletedPanelId(): string {
    for (let n = this.panels.length -1; n >=0; n--) {
        if(this.panels[n].icon === 'success' && this.panels[n].stepProgress === 'COMPLETED' && this.panels[n].isVisible) {
          return this.panels[n].id;
        }
    }
    return this.panels[0].id;
  }

  public getClosestPanel(selectedPanelId: string) {
    let panel: LandmarkPanel;

    for (let i = 0, len = this._panels.length; i < len; i++) {
      panel = this._panels[ i ];

      // skip subPanels, they were added by their parent
      if (panel.isSubPanel) {
        continue;
      }

      if (panel.id === selectedPanelId) {
        // get selected index in this._panels
        // get index and then find next valid panel or go previous panels
        const nextPanelIndex = this.getNextAvailablePanelIndex(panel.index);
        if (nextPanelIndex === -1) {
          const previousPanelIndex = this.getPreviousAvailablePanelIndex(panel.index);
          if (previousPanelIndex === -1) {
            return null;
          } else {
            return this._panels[ previousPanelIndex ].id;
          }
        } else {
          return this._panels[ nextPanelIndex ].id;
        }
        return null;
      }
    }
  }

  private updateDashBoardPanels(busFormSpec: LMBusFormSpec, model: LMModel, dashboardPanel: LandmarkPanel): void {
    const panelSpec = dashboardPanel.busFormPanel;

    dashboardPanel.hidden = false;
    dashboardPanel.isVisible = true;
    dashboardPanel.title = this.lmFormatUtilService.formatMessageRule(panelSpec.panelTitle, model, busFormSpec.fields);

    if (this.lmWebAppService.isFormPanelInaccessible(panelSpec)) {
      dashboardPanel.hidden = true;
    }

    if (panelSpec.visibility && !this.lmConditionUtilService.isValid(panelSpec.visibility, model)) {
      dashboardPanel.hidden = true;
      dashboardPanel.isVisible = false;
    }

    let validPanel = (panelSpec.validity) ? this.lmConditionUtilService.isValid(panelSpec.validity, model) : true;
    if (model.isCreateAction && this.isOTMPanel(panelSpec, model)) {
      validPanel = false;
    }

    dashboardPanel.isValid = validPanel;
  }

  public createSubPanels(panels: Array<LMCompositePanelSpec>, busFormSpec: LMBusFormSpec, model: LMModel, formSpec: LMFormSpec): Array<LandmarkPanel> {
    let panel: LandmarkPanel;
    const panelSpecs: Array<LMCompositePanelSpec> = panels;
    let panelSpec: LMCompositePanelSpec;
    const dataView = model.getData();

    const subPanels = this.buildInitialCompositePanels(busFormSpec, model, formSpec, panels, true);

    for (let i = 0, len = panels.length; i < len; i++) {
      panel = subPanels[ i ];
      panel.type = panels[ i ].panelType;

      // skip subPanels, they were added by their parent
      if (panel.isSubPanel) {
        continue;
      }

      panelSpec = panelSpecs[ i ];

      // wizard form has a finish form that is not included in the panelSpec collection, the finish form should
      // always be added to the list of panels

      panel.hidden = false;

      if (panelSpec) {
        panel.title = this.lmFormatUtilService.formatMessageRule(panelSpec.panelTitle, model);
      }

      if (this.lmWebAppService.isFormPanelInaccessible(panels[ i ])) {
        panel.hidden = true;
      }

      if (panelSpec && panelSpec.visibility && !this.lmConditionUtilService.isValid(panelSpec.visibility, model)) {
        panel.hidden = true;
      }

      let validPanel = (panelSpec && panelSpec.validity) ? this.lmConditionUtilService.isValid(panelSpec.validity, model) : true;

      if (panelSpec) {
        if (model.isCreateAction && this.isOTMPanel(panelSpec, model)) {
          validPanel = false;
        }
      }

      if (!validPanel && (busFormSpec.panelNavigation || busFormSpec.isWizard)) {
        panel.hidden = true;
      }

      panel.isValid = validPanel;

      panel.isVisible = panel.isValid && !panel.hidden;

      if (panelSpec && (busFormSpec.panelNavigation || busFormSpec.isWizard)) {
        this.updateStepIcon(panel, dataView);
      }
    }

    return subPanels;
  }

  private isOTMPanel(panelSpec: LMCompositePanelSpec, model: LMModel): boolean {
    if (panelSpec.hasSubPanels) {
      return true;
    }

    return  (panelSpec.panelType !== AppConstants.FORM_PANELTYPE_FORM);
  }

  private addPanelSubPanels(busFormSpec: LMBusFormSpec, panel: LandmarkPanel) {
    for (let i = 0, len = panel.subPanels.length; i < len; i++) {
      const subPanel: LandmarkPanel = panel.subPanels[ i ];

      const parentPanelIndex = this.getPanelIndexById(panel.id);

      if (parentPanelIndex < this._furthestPanelIndex || (panel.linearVisited && i <= this._furthestSubPanelIndex)) {
        subPanel.linearVisited = true;
      }

      if (panel.busFormPanel.subPanelSpec.validity) {
        subPanel.isValid = this.lmConditionUtilService.isValid(panel.busFormPanel.subPanelSpec.validity, subPanel.subPanelDataView);
      }

      if (panel.busFormPanel.subPanelSpec.mouseOverText) {
        subPanel.mouseOverText = this.lmFormatUtilService.formatMessageRule(panel.busFormPanel.subPanelSpec.mouseOverText, subPanel.subPanelDataView);
      }
      this.panels.push(subPanel);
      if (busFormSpec.panelNavigation || busFormSpec.isWizard) {
        this.updateStepIcon(subPanel, subPanel.subPanelDataView);
      }
    }
  }

  private updateStepProgress(panel: LandmarkPanel, dataView: LMDataView) {
    if (panel.busFormPanel.complete && this.lmConditionUtilService.isValid(panel.busFormPanel.complete, dataView)) {
      panel.stepProgress = 'COMPLETED';
    } else if (panel.busFormPanel.progress && this.lmConditionUtilService.isValid(panel.busFormPanel.progress, dataView)) {
      panel.stepProgress = 'IN_PROGRESS';
    } else {
      panel.stepProgress = 'NONE';
    }
  }

  private updateStepAlertInfo(panel: LandmarkPanel, dataView: LMDataView): boolean {
    if (panel.busFormPanel.alert) {
      if (panel.busFormPanel.redAlert && this.lmConditionUtilService.isValid([ panel.busFormPanel.redAlert.condition ], dataView)) {
        panel.stepAlert = 'RED';
        if (panel.busFormPanel.redAlert.alertMouseOverMessage) {
          panel.stepAlertMouseOver = this.lmFormatUtilService.formatMessageRule(panel.busFormPanel.redAlert.alertMouseOverMessage, dataView);
        }
        return true;
      } else if (panel.busFormPanel.yellowAlert && this.lmConditionUtilService.isValid([ panel.busFormPanel.yellowAlert.condition ], dataView)) {
        panel.stepAlert = 'YELLOW';
        if (panel.busFormPanel.yellowAlert.alertMouseOverMessage) {
          panel.stepAlertMouseOver = this.lmFormatUtilService.formatMessageRule(panel.busFormPanel.yellowAlert.alertMouseOverMessage, dataView);
        }
        return true;
      } else if (panel.busFormPanel.greenAlert && this.lmConditionUtilService.isValid([ panel.busFormPanel.greenAlert.condition ], dataView)) {
        panel.stepAlert = 'GREEN';
        if (panel.busFormPanel.greenAlert.alertMouseOverMessage) {
          panel.stepAlertMouseOver = this.lmFormatUtilService.formatMessageRule(panel.busFormPanel.greenAlert.alertMouseOverMessage, dataView);
        }
        return true;
      } else if (panel.busFormPanel.blueAlert && this.lmConditionUtilService.isValid([ panel.busFormPanel.blueAlert.condition ], dataView)) {
        panel.stepAlert = 'BLUE';
        if (panel.busFormPanel.blueAlert.alertMouseOverMessage) {
          panel.stepAlertMouseOver = this.lmFormatUtilService.formatMessageRule(panel.busFormPanel.blueAlert.alertMouseOverMessage, dataView);
        }
        return true;
      } else if (panel.busFormPanel.iconAlert && this.lmConditionUtilService.isValid([ panel.busFormPanel.iconAlert.condition ], dataView)) {
        panel.stepAlert = 'ICON';
        if (panel.busFormPanel.iconAlert.alertMouseOverMessage) {
          panel.stepAlertMouseOver = this.lmFormatUtilService.formatMessageRule(panel.busFormPanel.iconAlert.alertMouseOverMessage, dataView);
        }
        return true;
      }
    }
    return false;
  }

  public updateStepIcon(panel: LandmarkPanel, dataView: LMDataView) {
    this.updateStepProgress(panel, dataView);
    this.updateStepAlertInfo(panel, dataView);

    if (this.updateStepCompletedIcon(panel)) {
      return;
    }

    if (this.updateStepAlertIcon(panel)) {
      return;
    }

    if (this.updateStepInProgessIcon(panel)) {
      return;
    }

    panel.icon = AppConstants.STEP_PROCESS_UNVISITED_ICON;
    panel.iconInfo = null;
  }

  private updateStepCompletedIcon(panel: LandmarkPanel): boolean {
    if (panel.stepProgress === 'COMPLETED') {
      panel.icon = AppConstants.STEP_PROCESS_COMPLETE_ICON;
      panel.iconInfo = null;
      return true;
    } else {
      return false;
    }
  }

  private updateStepInProgessIcon(panel: LandmarkPanel): boolean {
    if (panel.stepProgress === 'IN_PROGRESS') {
      panel.icon = AppConstants.STEP_PROCESS_INPROCESS_ICON;
      panel.iconInfo = null;
      return true;
    } else {
      return false;
    }
  }

  private updateStepAlertIcon(panel: LandmarkPanel): boolean {
    if (panel.stepAlert === 'RED') {
      panel.icon = 'error';
      panel.iconInfo = panel.stepAlertMouseOver;
      return true;
    }

    if (panel.stepAlert === 'YELLOW') {
      panel.icon = 'alert';
      panel.iconInfo = panel.stepAlertMouseOver;
      return true;
    }

    if (panel.stepAlert === 'GREEN') {
      panel.icon = 'success';
      panel.iconInfo = panel.stepAlertMouseOver;
      return true;
    }

    if (panel.stepAlert === 'BLUE') {
      panel.icon = 'info';
      panel.iconInfo = panel.stepAlertMouseOver;
      return true;
    }

    // NOT SUPPORTED, SO USING info icon for now
    if (panel.stepAlert === 'ICON') {
      panel.icon = 'info';
      panel.iconInfo = panel.stepAlertMouseOver;
      return true;
    }

    panel.iconInfo = null;
    return false;
  }

  public buildCompositePanels(busFormSpec: LMBusFormSpec, model: LMModel, formSpec: LMFormSpec, compositeForm?: boolean): void {
    this.panels = [];
    this._panels = [];

    const panelSpecs: Array<LMCompositePanelSpec> = busFormSpec.panels;

    this._panels = this.buildInitialCompositePanels(busFormSpec, model, formSpec, panelSpecs, compositeForm);

    this.updateCompositePanels(busFormSpec, model, false, compositeForm);

    if (busFormSpec.contextForm) {
      this.contextForm = busFormSpec.contextForm;
    }

    if (!busFormSpec.panelNavigation && !busFormSpec.isWizard) {
      this.verticalTabs = (busFormSpec.showTabsOnLeft) ? busFormSpec.showTabsOnLeft : false;

    }
  }

  public buildSubStepPanels(panelSpec: LMCompositePanelSpec, landmarkPanel: LandmarkPanel, dataViewSet: LMDataViewSet): Array<LandmarkPanel> {
    const subPanels: Array<LandmarkPanel> = [];
    for (let i = 0, len = dataViewSet.data.length; i < len; i++) {
      subPanels.push(this.buildSubStepDynamicPanel(panelSpec, landmarkPanel, dataViewSet.data[ i ], i));
    }

    return subPanels;
  }

  private buildSubStepDynamicPanel(panelSpec: LMCompositePanelSpec, landmarkPanel: LandmarkPanel, subPanelDataView: LMDataView, index: number): LandmarkPanel {
    let panel: LandmarkPanel;
    const title: string = subPanelDataView.title;  // DPB this is odd that it was added to the LMDataView should be handle on the browser client like other messagerules

    // find the first valid subPanel spec
    for (let i = 0, len = panelSpec.subPanelSpec.formSubPanels.length; i < len; i++) {

      // create a deep clone of the spec so we can add a unique namePrefix; see also lm-form-content-template.component.ts
      // TODO: future consideration for refactoring: use formOptions to pass form / panel id so deep-cloning would not be needed
      const subPanelSpec = JSON.parse(JSON.stringify(panelSpec.subPanelSpec.formSubPanels[ i ])) as LMCompositePanelSpec;
      const panelType = subPanelSpec.panelType;

      if (subPanelSpec.displayWhen
      && !this.lmConditionUtilService.isValid(subPanelSpec.displayWhen, subPanelDataView)) {
        continue;
      }

      const panelId = subPanelSpec.panelId + '_' + index;

      panel = {
        id: panelId,
        automationId: subPanelSpec.panelId,
        title,
        content: '',
        isVisible: true,
        isValid: true,
        isSubPanel: true,
        displayWhenIndex: i,
        parentId: landmarkPanel.id,
        subPanelDataView,
        busFormPanel: subPanelSpec,
        panelText: subPanelSpec.panelText,
        icon: AppConstants.STEP_PROCESS_UNVISITED_ICON,
        // onCompositeForm: compositeForm // DPB this needs to be understood
      };

      const parentPanelIndex = this.getPanelIndexById(landmarkPanel.id);

      if (parentPanelIndex < this._furthestPanelIndex || (landmarkPanel.linearVisited && index <= this._furthestSubPanelIndex)) {
        panel.linearVisited = true;
      }

      if (panelSpec.subPanelSpec.validity) {
        panel.isValid = this.lmConditionUtilService.isValid(panelSpec.subPanelSpec.validity, subPanelDataView);
      }

      if (panelSpec.subPanelSpec.mouseOverText) {
        panel.mouseOverText = this.lmFormatUtilService.formatMessageRule(panelSpec.subPanelSpec.mouseOverText, subPanelDataView);
      }

      if (panelType === AppConstants.FORM_PANELTYPE_ACTIONIS) {
        // add panelId to namePrefix to ensure field id uniqueness
        subPanelSpec.formSpec.busFormSpec.namePrefix = panelId;

        panel.index = i;
        panel.initialized = false;
        panel.loaded = false;
        panel.reload = false;
        panel.disabled = false;
        panel.hidden = false;
        panel.busFormPanel = subPanelSpec;
        panel.formSpec = subPanelSpec.formSpec;
        panel.type = panelType;
      } else if (panelType === AppConstants.FORM_PANELTYPE_FORMLIST) {
        panel.index = i;
        panel.initialized = false;
        panel.loaded = false;
        panel.reload = false;
        panel.disabled = false;
        panel.hidden = false;
        panel.listSpec = subPanelSpec.listSpec;
        panel.type = subPanelSpec.panelType;
      } else if (panelType === AppConstants.FORM_PANELTYPE_MULTILIST) {
        panel.index = i;
        panel.initialized = false;
        panel.loaded = false;
        panel.reload = false;
        panel.disabled = false;
        panel.hidden = false;
        panel.busFormPanel = subPanelSpec;
        panel.sections = subPanelSpec.lists;
        panel.type = panelType;
      } else if (panelType === AppConstants.FORM_PANELTYPE_TEMPLATEFORM) {
        panel.index = i;
        panel.initialized = false;
        panel.loaded = false;
        panel.reload = false;
        panel.disabled = false;
        panel.hidden = false;
        panel.busFormPanel = subPanelSpec;
        panel.layout = subPanelSpec.formSpec.busFormSpec.busFormLayoutSpec.layout;
        panel.formSpec = subPanelSpec.formSpec;
        panel.type = panelType;
      } else if (panelType === AppConstants.FORM_PANELTYPE_URL) {
        panel.index = i;
        panel.initialized = false;
        panel.loaded = false;
        panel.reload = false;
        panel.disabled = false;
        panel.hidden = false;
        panel.type = panelSpec.panelType;
        panel.url = subPanelSpec.URL;
        panel.type = panelType;
      } else {
        panel.index = i;
        panel.initialized = false;
        panel.loaded = false;
        panel.reload = false;
        panel.disabled = false;
        panel.hidden = false;
        panel.type = subPanelSpec.panelType;
        panel.validity = subPanelSpec.validity;
      }

      this.updateStepIcon(panel, subPanelDataView);
      return panel;
    }
  }

  private updateFieldIdsMap(busFormSpec: LMBusFormSpec, layout: LMFormLayout, panelId: string) {
    if (busFormSpec.implementsDashBoardView) {
      return;
    }

    // need to add specFields? not sure, issue to determine which panel compound field is on
    this.lmCommonUtilService.getBindIdsFromLayout(layout).forEach( fieldId => {
      if (this._mapFieldIds[ fieldId ]) {
        if (!this._mapFieldIds[ fieldId ].includes(panelId)) {
          this._mapFieldIds[ fieldId ].push(panelId);
        }

      } else {
        this._mapFieldIds[ fieldId ] = [ panelId ];
      }
    });
  }

  // LMWEB-18421 - Using the old method for composite
  public getPanelIdsForFieldIdComposite(fieldId: string) {
    if (this._mapFieldIds && this._mapFieldIds[ fieldId ]) {
      return this._mapFieldIds[ fieldId ];
    }

    return [];
  }

  // LMWEB-17385 - Using the new method for step panel
  public getPanelIdsForFieldId(fieldId: string) {
    if (this._mapFieldIds && this._mapFieldIds[ fieldId ]) {
      const errorPanels = this._mapFieldIds[ fieldId ];
      const validErrorPanels: Array<string> = [];

      for (let i = 0, len = errorPanels.length; i < len; i++) {
        const validPanel = this.getValidVisiblePanelIndexById(errorPanels[ i ]);
        if (validPanel !== -1) {
          validErrorPanels.push(errorPanels[ i ]);
          return validErrorPanels;
        }
      }
    }

    return [];
  }

  private buildInitialCompositePanels(busFormSpec: LMBusFormSpec, model: LMModel, formSpec: LMFormSpec, panelSpecs: Array<LMCompositePanelSpec>, compositeForm?: boolean): Array<LandmarkPanel> {
    let panel: LandmarkPanel;
    const panels: Array<LandmarkPanel> = [];
    let finishFormIndex = 0;

    for (let i = 0, len = panelSpecs.length; i < len; i++) {
      finishFormIndex = i + 1;
      const panelSpec = panelSpecs[ i ];
      const title = this.lmFormatUtilService.formatMessageRule(panelSpec.panelTitle, model);
      const panelType = panelSpec.panelType;

      panel = {
        id: panelSpec.panelId,
        automationId: panelSpec.automationId,
        title,
        content: '',
        isVisible: true,
        isValid: true,
        onCompositeForm: compositeForm,
        busFormPanel: panelSpec,
        hidden: false,
        panelText: panelSpec.panelText,
        panelHeader: panelSpec.panelHeader,
        visibility: panelSpec.visibility,
        widgetWidth: this.lmSpecService.toWidgetSizeToType(panelSpec.dashboardViewWidth),
        widgetHeight: this.lmSpecService.toWidgetSizeToType(panelSpec.dashboardViewHeight),
        spanningColumns: panelSpec.twelveColumnGridSpan? panelSpec.twelveColumnGridSpan : undefined
      };

      if (panelSpec.specialActions) {
        panel.specialActions = panelSpec.specialActions;
      }

      if (panelSpec.overviewNavigation) {
        panel.overviewNavigation = panelSpec.overviewNavigation;
      }

      if (panelType === AppConstants.FORM_PANELTYPE_FORM) {
        panel.index = i;
        panel.initialized = false;
        panel.loaded = false;
        panel.reload = false;
        panel.disabled = false;
        panel.hidden = false;
        panel.layout = panelSpec.busFormLayoutSpec.layout;
        panel.formSpec = formSpec;
        panel.type = panelType;
        panel.saveOnNext = panelSpec.saveOnNext;

        // maps a panelId to a fieldId; used when lazyloadform is true and needed when an error is set on a field where
        // a panel has not been initialized
        this.updateFieldIdsMap(busFormSpec, panelSpec.busFormLayoutSpec.layout, panel.id);

      } else if (panelType === AppConstants.FORM_PANELTYPE_INDEPENDENTFORM) {
        panel.index = i;
        panel.initialized = false;
        panel.loaded = false;
        panel.reload = false;
        panel.disabled = false;
        panel.hidden = false;
        panel.formSpec = panelSpec.formSpec;

        // add panelId to namePrefix to ensure field id uniqueness
        panel.formSpec.busFormSpec.namePrefix = panelSpec.panelId;
        panel.type = panelType;
      } else if (panelType === AppConstants.FORM_PANELTYPE_ACTIONIS) {
        // todo spec-url-refactor dboehm what do to do here?
        panel.index = i;
        panel.initialized = false;
        panel.loaded = false;
        panel.reload = false;
        panel.disabled = false;
        panel.hidden = false;
        panel.formSpec = panelSpec.formSpec;
        panel.type = panelType;
      } else if (panelType === AppConstants.FORM_PANELTYPE_FORMLIST) {
        panel.index = i;
        panel.initialized = false;
        panel.loaded = false;
        panel.reload = false;
        panel.disabled = false;
        panel.hidden = false;
        panel.listSpec = panelSpec.listSpec;
        panel.listSpec.busListSpec.disableFieldOptions = panelSpec.listSpec.busListSpec.disableFieldOptions || formSpec.disableFieldOptions;
        panel.type = panelSpec.panelType;
      } else if (panelType === AppConstants.FORM_PANELTYPE_MULTILIST) {
        panel.index = i;
        panel.initialized = false;
        panel.loaded = false;
        panel.reload = false;
        panel.disabled = false;
        panel.hidden = false;
        panel.sections = panelSpec.lists;
        panel.type = panelType;
      } else if (panelType === AppConstants.FORM_PANELTYPE_TEMPLATEFORM) {
        panel.index = i;
        panel.initialized = false;
        panel.loaded = false;
        panel.reload = false;
        panel.disabled = false;
        panel.hidden = false;
        panel.layout =  panelSpec.formSpec.busFormSpec.busFormLayoutSpec ? panelSpec.formSpec.busFormSpec.busFormLayoutSpec.layout : null;
        panel.formSpec = panelSpec.formSpec;
        panel.type = panelType;
      } else if (panelType === AppConstants.FORM_PANELTYPE_URL) {
        panel.index = i;
        panel.initialized = false;
        panel.loaded = false;
        panel.reload = false;
        panel.disabled = false;
        panel.hidden = false;
        panel.type = panelSpec.panelType;
        panel.url = panelSpec.URL;
      } else {
        panel.id = panelSpec.panelId;
        panel.index = i;
        panel.initialized = false;
        panel.loaded = false;
        panel.reload = false;
        panel.disabled = false;
        panel.hidden = false;
        panel.type = panelSpec.panelType;
        panel.validity = panelSpec.validity;
        panel.useTwelveColumnGrid = panelSpec.useTwelveColumnGrid;
      }
      // add default icon for step process panels
      if (busFormSpec.panelNavigation) {
        panel.icon = AppConstants.STEP_PROCESS_UNVISITED_ICON;
      }

      panel.hasSubPanels = panelSpec.hasSubPanels;

      if (panelSpec.hasSubPanels) {
        panel.subPanels = new Array<LandmarkPanel>();
        panel.busFormPanel = panelSpec;
      }

      panels.push(panel);
    }

    if ((busFormSpec.panelNavigation || busFormSpec.isWizard) && busFormSpec.wizardFinishForm) {
      if (busFormSpec.wizardFinishForm.formType === 'Summary') {
        this.lmLoggerService.log('Summary Form not allowed');
      } else {

        const finishFormTitle = this.lmFormatUtilService.formatMessageRule(busFormSpec.wizardFinishForm.title, model);
        const formOptions: LMFormOptions = {
          isReadOnlyForm: true
        };

        // Add wizard finish form
        const finishFormPanel: LandmarkPanel = {
          id: busFormSpec.wizardFinishForm.name,
          title: finishFormTitle,
          content: '',
          isVisible: true,
          isValid: true,
          index: finishFormIndex,
          initialized: false,
          // add default icon
          icon: AppConstants.STEP_PROCESS_UNVISITED_ICON,
          loaded: false,
          reload: false,
          disabled: false,
          hidden: false,
          type: 'form',
          layout: busFormSpec.wizardFinishForm.busFormLayoutSpec.layout,
          // validity: true, // validity is of type LMCondition[]. We can make it a boolean too but not sure what that means.
          finishForm: true,
          formSpec,   // busFormSpec.wizardFinishForm,
          formOptions
        };

        panels.push(finishFormPanel);
      }
    }

    return panels;
  }

  public getPanelTransaction(options: LMGetPanelTransactionOptions): PanelTransactionEnum {
    const busFormSpec: LMBusFormSpec = options.busFormSpec;
    const currentPanel: LandmarkPanel = options.currentPanel;
    const targetPanel: LandmarkPanel = options.targetPanel;
    const isNext: boolean = options.isNext;
    const isPanelDirty: boolean = options.isPanelDirty;
    const isCreateMode: boolean = options.isCreateMode;
    const enforceLinearWhen = options.enforceLinearWhen;

    // enforce linear when kicks off the validation process only when the 'Next' action is used!!!
    if (enforceLinearWhen) {
      let panelTransaction = PanelTransactionEnum.FORM_PANEL_TRANSACTION_NONE_TYPE;

      if (isNext) {

        if (isPanelDirty || isCreateMode) {
          panelTransaction = PanelTransactionEnum.FORM_PANEL_TRANSACTION_VALIDATION_TYPE;
        }

        if (currentPanel.type !== AppConstants.FORM_PANELTYPE_FORM || currentPanel.saveOnNext || this.isCrossingBoundary(busFormSpec, currentPanel, targetPanel, isNext)) {
          panelTransaction = PanelTransactionEnum.FORM_PANEL_TRANSACTION_COMMIT_TYPE;
        }
      }
      return panelTransaction;
    }

    // leaving the existing logic unchanged, goal is to deprecate this behavior!!!
    if (currentPanel.type === AppConstants.FORM_PANELTYPE_ACTIONIS
      || currentPanel.type === AppConstants.FORM_PANELTYPE_URL) {
      return  (isNext
        ? PanelTransactionEnum.FORM_PANEL_TRANSACTION_COMMIT_TYPE
        : PanelTransactionEnum.FORM_PANEL_TRANSACTION_NONE_TYPE);
    } else if (currentPanel.type !== AppConstants.FORM_PANELTYPE_FORM) {
      return (isPanelDirty
        ? PanelTransactionEnum.FORM_PANEL_TRANSACTION_COMMIT_TYPE
        : PanelTransactionEnum.FORM_PANEL_TRANSACTION_NONE_TYPE);
    } else {
      if (isNext && currentPanel.saveOnNext && (isPanelDirty || isCreateMode)) {
        return PanelTransactionEnum.FORM_PANEL_TRANSACTION_COMMIT_TYPE;
      }
      if (this.isCrossingBoundary(busFormSpec, currentPanel, targetPanel, isNext)) {
        return (isCreateMode || isPanelDirty                           // commit
          ? PanelTransactionEnum.FORM_PANEL_TRANSACTION_COMMIT_TYPE
          : PanelTransactionEnum.FORM_PANEL_TRANSACTION_NONE_TYPE);
      } else {
        return (isCreateMode || isPanelDirty                           // validate
          ? PanelTransactionEnum.FORM_PANEL_TRANSACTION_VALIDATION_TYPE
          : PanelTransactionEnum.FORM_PANEL_TRANSACTION_NONE_TYPE);
      }
    }
  }

  private isCrossingBoundary(busFormSpec: LMBusFormSpec, currentPanel: LandmarkPanel, targetPanel: LandmarkPanel, isNext: boolean) {
    if (busFormSpec.isWizard) {
      return this.isCrossingWizardBoundary(currentPanel, targetPanel, isNext);
    } else {
      return this.isCrossingStepBoundary(currentPanel, targetPanel, isNext);
    }
  }

  private isCrossingWizardBoundary(currentPanel: LandmarkPanel, targetPanel: LandmarkPanel, isNext: boolean): boolean {
    /*
     if the target step is the summary panel
     you are crossing a boundary

     if the current step is not a AppConstants.FORM_PANELTYPE_FORM
     you are crossing a boundary

     if the current step is a AppConstants.FORM_PANELTYPE_FORM and
     the target step is not a AppConstants.FORM_PANELTYPE_FORM and
     the navigation is to the next step.
     you are crossing a boundary
     */

    if ((targetPanel == null) || (targetPanel.id === currentPanel.id) || (targetPanel.finishForm)
      || (targetPanel.type !== AppConstants.FORM_PANELTYPE_FORM)
      || (isNext && currentPanel.type === AppConstants.FORM_PANELTYPE_FORM && targetPanel.type !== AppConstants.FORM_PANELTYPE_FORM)) {
      return true;
    } else {
      return false;
    }
  }

  private isCrossingStepBoundary(currentPanel: LandmarkPanel, targetPanel: LandmarkPanel, isNext: boolean): boolean {
    /*
       if the target step is the summary panel
       you are crossing a boundary

       if the current step is not a AppConstants.FORM_PANELTYPE_FORM
       you are crossing a boundary

       if the current step is a AppConstants.FORM_PANELTYPE_FORM and
       the target step is not a AppConstants.FORM_PANELTYPE_FORM
       you are crossing a boundary
       */

    // if ((targetPanel == null) || (targetPanel.finishForm)
    //   || (currentPanel.type !== AppConstants.FORM_PANELTYPE_FORM)
    //   || (currentPanel.type === AppConstants.FORM_PANELTYPE_FORM && targetPanel.type !== AppConstants.FORM_PANELTYPE_FORM)) {
    //   return true;
    // } else {
    //   return false;
    // }

    return true; // in 3.5 landmarksubstep.js it always returns true
  }

  public requiresAcknowledgement(panel: LandmarkPanel, dataView: LMDataView): boolean {
    if (panel.hasSubPanels) {      // DONT run acknowledgment on a subpanel header
      return false;
    }

    if (!panel.busFormPanel) {
      return false; // must be a summary form on a wizard
    }

    if (panel.busFormPanel.acknowledgeAction && panel.busFormPanel.acknowledgeCompletedWhen) {
      return !this.lmConditionUtilService.isValid(panel.busFormPanel.acknowledgeCompletedWhen, dataView);
    } else {
      return panel.busFormPanel.acknowledgeAction ? true : false;
    }
  }

  public refreshSubPanelViews(formUrl: string, dataViews: LMDataView[]): Observable<any> {
    return this.lmFormRequestService.refreshSubPanelViews(formUrl, dataViews);
  }

  /**
   * Get the selected panel either from an url hash or pagestate.
   * @param formUrlInfo
   * @param formState
   */
  public getPanelIndexToSelect(formUrlInfo: LMFormUrlInfo, formState: LMFormState) {
    let selectedPanelIndex = formState.selectedPanelIndex;

    const targetPanelName = formUrlInfo.urlParams['selectedPanel'];  // this.lmUrlUtilService.getUrlParam(formUrlInfo.httpUrl, 'selectedPanel');
    if (targetPanelName) {
      const targetPanel = this.panels.find((panelToFind: LandmarkPanel) => panelToFind.busFormPanel.panelName === targetPanelName);
      if (targetPanel) {
        const targetPanelIndex = this.panels.indexOf(targetPanel);

        // if the panel is found set the formState.selectedPanelIndex to its index.
        selectedPanelIndex = targetPanelIndex;
      }
    }

    if (selectedPanelIndex === undefined || selectedPanelIndex >= this.panels.length || selectedPanelIndex < 0) {
      selectedPanelIndex = 0;
    } else {
      // if formState.panel is hidden then get the next best valid panel
      const selectedPanel = this.panels[ selectedPanelIndex ];
      if (selectedPanel && selectedPanel.hidden) {
        for (let i = 0, length = this.panels.length; i < length; i++) {
          if (!this.panels[ i ].hidden) {
            this.panels[ i ].selected = true;
            selectedPanelIndex = i;
            break;
          }
        }
      } else {
        if (selectedPanel) {
          selectedPanel.selected = true;
        }
      }
    }

    formState.selectedPanelIndex = selectedPanelIndex;
    return selectedPanelIndex;
  }

  // public getPanelIndexToSelect(formUrlInfo: LMFormUrlInfo, formState: LMFormState) {
  //   let selectedPanelIndex = formState.selectedPanelIndex;
  //
  //   if (formUrlInfo?.url?.hash) {
  //     // if there's a panel hash on the url find the panel for that hash
  //     const targetPanelName = formUrlInfo.url.hash.substring(1);
  //     const targetPanel = this.panels.find((panelToFind: LandmarkPanel) => panelToFind.busFormPanel.panelName === targetPanelName);
  //     if (targetPanel) {
  //       const targetPanelIndex = this.panels.indexOf(targetPanel);
  //
  //       // if the panel is found set the formState.selectedPanelIndex to its index.
  //       selectedPanelIndex = targetPanelIndex;
  //     }
  //   }
  //
  //   if (selectedPanelIndex === undefined || selectedPanelIndex >= this.panels.length || selectedPanelIndex < 0) {
  //     selectedPanelIndex = 0;
  //   } else {
  //     // if formState.panel is hidden then get the next best valid panel
  //     const selectedPanel = this.panels[ selectedPanelIndex ];
  //     if (selectedPanel && selectedPanel.hidden) {
  //       for (let i = 0, length = this.panels.length; i < length; i++) {
  //         if (!this.panels[ i ].hidden) {
  //           this.panels[ i ].selected = true;
  //           selectedPanelIndex = i;
  //           break;
  //         }
  //       }
  //     } else {
  //       if (selectedPanel) {
  //         selectedPanel.selected = true;
  //       }
  //     }
  //   }
  //
  //   formState.selectedPanelIndex = selectedPanelIndex;
  //   return selectedPanelIndex;
  // }
}

export type PanelTransaction = 'commit' | 'validation' | 'none';

export enum PanelTransactionEnum {
  FORM_PANEL_TRANSACTION_COMMIT_TYPE = 'commit',
  FORM_PANEL_TRANSACTION_VALIDATION_TYPE = 'validation',
  FORM_PANEL_TRANSACTION_NONE_TYPE = 'none'
}

export interface LMGetPanelTransactionOptions {
  busFormSpec: LMBusFormSpec,
  currentPanel: LandmarkPanel,
  targetPanel: LandmarkPanel,
  isNext: boolean,
  isPanelDirty: boolean,
  isCreateMode: boolean,
  enforceLinearWhen: boolean
}

export class LMStepPanelEvent {
  private _panelId:              string;
  private _panelTransaction:     PanelTransaction;
  private _transactionObserver:  Observer<boolean>;

  constructor(
    panelId: string,
    panelTransaction: PanelTransaction,
    transactionObserver: Observer<boolean>) {
    this._panelId = panelId;
    this._panelTransaction = panelTransaction;
    this._transactionObserver = transactionObserver;
  }

  get panelId(): string {
    return this._panelId;
  }

  get panelTransaction(): PanelTransaction {
    return this._panelTransaction;
  }

  get transactionObserver(): Observer<boolean> {
    return this._transactionObserver;
  }
}

export class LMPanelTransactionEvent implements LMModelSourceEvent {
  private _originatingPanel: string;
  private _originatingList:  string;
  private _callBack: Observer<boolean>;

  constructor(
    originatingPanel: string) {
    this._originatingPanel = originatingPanel;
  }

  get type(): LMModelSourceType {
    return 'transaction';
  }
  get originatingPanel(): string {
    return this._originatingPanel;
  }

  get originatingList(): string {
    return this._originatingList;
  }

  set originatingList(originatingList: string) {
    this._originatingList = originatingList;
  }

  get callBack(): Observer<boolean> {
    return this._callBack;
  }

  set callBack(callBack: Observer<boolean>) {
    this._callBack = callBack;
  }
}
